<script setup lang="ts">
  import { ref, computed, onMounted, watch, type PropType } from 'vue'
  import { MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'
  import InputField from '@/components/admin/form/InputField.vue'
  import type { CustomerQuestionnaire } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import { debounce } from '@/lib/timing'

  const { t } = useTranslation({
    en: {
      results: 'results',
      previous: 'Previous',
      next: 'Next',
      of: 'of',
      questionnaires: 'Questionnaires',
      searchQuestionnaires: 'Search questionnaires',
      selectAll: 'Select all',
      selected: 'selected',
      page: 'Page'
    },
    de: {
      page: 'Seite',
      results: 'Ergebnisse',
      previous: 'Vorherige',
      next: 'Nächste',
      of: 'von',
      questionnaires: 'Fragebogen',
      searchQuestionnaires: 'Fragebogen suchen',
      selectAll: 'Alle auswählen',
      selected: 'ausgewählt'
    }
  })

  const props = defineProps({
    questionnaires: {
      type: Array as PropType<CustomerQuestionnaire[]>,
      default: () => []
    },
    selectedQuestionnaireUuids: {
      type: Array,
      default: () => []
    },
    currentPage: {
      type: Number,
      default: 1
    },
    totalItems: {
      type: Number,
      default: 0
    },
    itemsPerPage: {
      type: Number,
      default: 10
    }
  })

  const emit = defineEmits(['update:selectedQuestionnaires', 'page-change', 'search-change'])

  const searchQuery = ref('')

  const debouncedEmitSearch = debounce((query: string) => {
    emit('search-change', query)
  }, 300)

  watch(searchQuery, (newValue) => {
    debouncedEmitSearch(newValue)
  })
  const currentPageInput = ref(String(props.currentPage))

  const selectedQuestionnaireIds = computed(() => new Set(props.selectedQuestionnaireUuids))

  const selectedCount = computed(() => selectedQuestionnaireIds.value.size)

  const isAllSelected = computed(() => {
    return selectedCount.value === props.questionnaires.length && props.questionnaires.length > 0
  })

  const goToPage = () => {
    const page = parseInt(currentPageInput.value)
    if (!isNaN(page) && page >= 1 && page <= Math.ceil(props.totalItems / props.itemsPerPage)) {
      emit('page-change', page)
    } else {
      currentPageInput.value = String(props.currentPage)
    }
  }

  const toggleAllQuestionnaires = () => {
    const newSelectedIds = isAllSelected.value
      ? []
      : props.questionnaires.map((doc: CustomerQuestionnaire) => doc.uuid)
    emit('update:selectedQuestionnaires', newSelectedIds)
  }

  const toggleQuestionnaire = (uuid: any) => {
    const newSelectedIds = new Set(selectedQuestionnaireIds.value)
    if (newSelectedIds.has(uuid)) {
      newSelectedIds.delete(uuid)
    } else {
      newSelectedIds.add(uuid)
    }
    console.log(Array.from(newSelectedIds))
    emit('update:selectedQuestionnaires', Array.from(newSelectedIds))
  }

  watch(
    () => props.currentPage,
    (newPage) => {
      currentPageInput.value = String(newPage)
    }
  )
</script>

<template>
  <div
    data-cy="questionnaire-template-section"
    class="mt-6 border border-gray-300 bg-white shadow sm:rounded-lg"
  >
    <div class="p-4">
      <h3 class="mb-2 text-lg font-semibold text-gray-900">{{ t('questionnaires') }}</h3>
      <div class="relative mb-2">
        <InputField
          data-cy="questionnaire-search-input"
          v-model="searchQuery"
          name="questionnaire-search"
          :placeholder="t('searchQuestionnaires')"
        >
          <template #append>
            <MagnifyingGlassIcon
              class="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400"
            />
          </template>
        </InputField>
      </div>
      <div class="flex items-center justify-between">
        <label class="flex items-center">
          <input
            data-cy="questionnaire-select-all"
            type="checkbox"
            :checked="isAllSelected"
            class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            @change="toggleAllQuestionnaires"
          />
          <span class="ml-2 text-sm font-medium text-gray-400">{{ t('selectAll') }}</span>
        </label>
        <span
          data-cy="questionnaire-selected-count"
          class="text-sm text-gray-500"
        >
          {{ selectedCount }} {{ t('selected') }}
        </span>
      </div>
      <div class="border-t border-gray-200"></div>
      <div class="max-h-60 overflow-y-auto">
        <template
          v-for="(doc, index) in props.questionnaires"
          :key="doc.uuid"
        >
          <div
            :data-cy="`questionnaire-item-${doc.uuid}`"
            class=""
          >
            <label class="flex items-center">
              <input
                :data-cy="`questionnaire-checkbox-${doc.uuid}`"
                type="checkbox"
                :checked="selectedQuestionnaireIds.has(doc.uuid)"
                class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                @change="toggleQuestionnaire(doc.uuid)"
              />
              <span class="ml-2 text-sm font-medium text-gray-700">{{ doc.title }}</span>
            </label>
          </div>
          <div
            v-if="index < props.questionnaires.length - 1"
            class="border-t border-gray-200"
          ></div>
        </template>
      </div>
      <!-- Pagination -->
      <div class="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
        <div class="flex items-center text-sm">
          <span class="text-gray-700">{{ t('page') }}</span>
          <input
            v-model="currentPageInput"
            type="text"
            class="mx-2 w-12 rounded-md border-gray-300 p-1 text-center text-sm"
            @change="goToPage"
          />
          <span class="text-gray-700">
            {{ t('of') }} {{ Math.ceil(props.totalItems / props.itemsPerPage) }}
          </span>
        </div>
        <div class="flex items-center space-x-2">
          <button
            :disabled="props.currentPage === 1"
            class="rounded p-1 hover:bg-gray-100 disabled:opacity-50"
            @click="emit('page-change', props.currentPage - 1)"
          >
            <ChevronLeftIcon class="h-5 w-5 text-gray-500" />
          </button>
          <button
            :disabled="props.currentPage >= Math.ceil(props.totalItems / props.itemsPerPage)"
            class="rounded p-1 hover:bg-gray-100 disabled:opacity-50"
            @click="emit('page-change', props.currentPage + 1)"
          >
            <ChevronRightIcon class="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
